import { ToastId, UseToastOptions } from '@chakra-ui/react'
import { useChakraToast } from '@core/ui-elements'
import React, { useCallback, useMemo } from 'react'

import { getIcon, ToastCardBase } from '../Toasts'
import { IToast, IToastCardBase } from '../Toasts.types'

export const useToast = () => {
  const chakraToast = useChakraToast()
  const { close, closeAll } = chakraToast

  const closeToast = useCallback(
    (id: ToastId) => {
      close(id)
    },
    [close]
  )

  const renderToast = useCallback(
    (
      props: Omit<UseToastOptions, 'icon' | 'variant'> & IToastCardBase
    ): ToastId => {
      const toastId = chakraToast({
        ...props,
        icon: null,
        render: () => (
          <ToastCardBase
            {...props}
            onClose={props.isClosable ? () => closeToast(toastId) : undefined}
          />
        ),
      })

      return toastId
    },
    [chakraToast, closeToast]
  )

  const toast: IToast = useMemo(
    () => ({
      success: ({ iconName = 'success-default', ...props }) =>
        renderToast({
          isClosable: true,
          ...props,
          icon: getIcon(iconName),
          variant: 'success',
        }),
      error: ({ iconName = 'error-default', ...props }) =>
        renderToast({
          isClosable: true,
          ...props,
          icon: getIcon(iconName),
          variant: 'error',
        }),
      warning: ({ iconName = 'warning-default', ...props }) =>
        renderToast({
          isClosable: true,
          ...props,
          icon: getIcon(iconName),
          variant: 'warning',
        }),
      info: ({ iconName = 'info-default', ...props }) =>
        renderToast({
          isClosable: true,
          ...props,
          icon: getIcon(iconName),
          variant: 'info',
        }),
      positive: ({ iconName = 'positive-default', ...props }) =>
        renderToast({
          isClosable: true,
          ...props,
          icon: getIcon(iconName),
          variant: 'positive',
        }),
    }),
    [renderToast]
  )

  return { toast, closeAll }
}
